import React, { useState } from 'react';
import { 
  Typography, 
  Paper, 
  Button, 
  Grid, 
  Card, 
  CardContent, 
  CardActions,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import StorageIcon from '@mui/icons-material/Storage';
import SettingsIcon from '@mui/icons-material/Settings';
import { getFunctions, httpsCallable } from 'firebase/functions';
import UserManagement from '../components/Settings/UserManagement';

const Settings = () => {
  const [conversionStatus, setConversionStatus] = useState({
    isRunning: false,
    result: null
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  const handleConvertHeicImages = async () => {
    try {
      // Ustaw status na "w trakcie"
      setConversionStatus({
        isRunning: true,
        result: null
      });

      // Wywołaj Cloud Function
      const functions = getFunctions();
      const convertHeicImagesFunc = httpsCallable(functions, 'convertHeicImages');
      const response = await convertHeicImagesFunc();
      
      // Pobierz wynik z odpowiedzi Cloud Function
      const result = response.data;
      
      // Ustaw wynik
      setConversionStatus({
        isRunning: false,
        result
      });

      // Pokaż komunikat
      setSnackbar({
        open: true,
        message: `Konwersja zakończona! Przekonwertowano ${result.convertedFiles} plików w ${result.updatedProducts} produktach.`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Błąd podczas konwersji:', error);
      
      setConversionStatus({
        isRunning: false,
        result: {
          success: false,
          error: error.message
        }
      });
      
      setSnackbar({
        open: true,
        message: `Błąd: ${error.message}`,
        severity: 'error'
      });
    }
  };

  return (
    <Paper sx={{ p: 3, height: '100%' }}>
      <Typography variant="h4" sx={{ mb: 4, fontFamily: 'Cinzel, serif', display: 'flex', alignItems: 'center', gap: 2 }}>
        <SettingsIcon /> Ustawienia Systemu
      </Typography>

      <Grid container spacing={3}>
        {/* Sekcja konwersji obrazów */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <ImageIcon /> Konwersja plików HEIC
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Ten moduł wyszukuje wszystkie produkty ze zdjęciami w formacie HEIC i konwertuje je do formatu JPEG.
                Nie zmienia to oryginalnych nazw plików, jedynie format. Proces może trwać od kilkunastu sekund do kilku minut,
                zależnie od ilości plików. Nowe produkty będą miały automatycznie konwertowane pliki HEIC.
              </Typography>
              
              {conversionStatus.result && conversionStatus.result.success && (
                <Alert severity="success" sx={{ my: 2 }}>
                  Przekonwertowano {conversionStatus.result.convertedFiles} plików 
                  w {conversionStatus.result.updatedProducts} produktach.
                </Alert>
              )}
              
              {conversionStatus.result && !conversionStatus.result.success && (
                <Alert severity="error" sx={{ my: 2 }}>
                  Błąd: {conversionStatus.result.error}
                </Alert>
              )}
            </CardContent>
            <CardActions>
              <Button 
                variant="contained" 
                onClick={handleConvertHeicImages}
                disabled={conversionStatus.isRunning}
                startIcon={conversionStatus.isRunning ? <CircularProgress size={20} /> : <StorageIcon />}
              >
                {conversionStatus.isRunning ? 'Konwersja w toku...' : 'Konwertuj pliki HEIC'}
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Sekcja zarządzania użytkownikami */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <UserManagement />
            </CardContent>
          </Card>
        </Grid>

        {/* Tutaj można dodać więcej modułów ustawień w przyszłości */}
      </Grid>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default Settings;
